import React, { useEffect, useState } from "react";
import { Topbar, Badge, DropdownMenu } from "@bsf/force-ui";
import { CircleHelp, FileText, Headset, User } from "lucide-react";
import { __ } from "@wordpress/i18n";
import { routes } from "../admin/settings/routes";
import { Link } from "../router/index";
import useWhatsNewRSS from "whats-new-rss";
function updateNavMenuActiveState() {
	const currentPath = window.location.hash;
	const menuItems = document.querySelectorAll(
		"#adminmenu #toplevel_page_uaepro a"
	);
	menuItems.forEach((item) => {
		const href = item.getAttribute("href");
		const parentLi = item.closest("li");
		const itemText = item.textContent.trim();
		if (
			href &&
			(currentPath.includes(href.split("#")[1]) ||
				("#dashboard" === currentPath && itemText === "Dashboard"))
		) {
			parentLi.classList.add("current");
		} else {
			parentLi.classList.remove("current");
		}
	});
}
export default function NavMenu() {
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [isHelpOpen, setIsHelpOpen] = useState(false);
	const toggleHelpDropdown = () => setIsHelpOpen((prev) => !prev);
	const closeHelpDropdown = () => setIsHelpOpen(false);
	useEffect(() => {
		updateNavMenuActiveState();
		window.addEventListener("hashchange", updateNavMenuActiveState);
		return () => {
			window.removeEventListener("hashchange", updateNavMenuActiveState);
		};
	}, []);
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (!event.target.closest(".uae-help-dropdown")) {
				setIsHelpOpen(false);
			}
		};
		document.addEventListener("click", handleClickOutside);
		return () => document.removeEventListener("click", handleClickOutside);
	}, []);
	const currentPath = window.location.hash;
	const isActive = (path) => currentPath.includes(path);
	const linkStyle = (path) => ({
		color: isActive(path) ? "#111827" : "#4B5563",
		borderBottom: isActive(path) ? "2px solid #6005FF" : "none",
		paddingBottom: "22px",
		marginBottom: "-16px",
	});
	const handleRedirect = (url) => {
		window.open(url, "_blank");
		setIsHelpOpen(false);
		setIsDropdownOpen(false);
	};
	useWhatsNewRSS({
		rssFeedURL: "https://ultimateelementor.com/whats-new/feed/",
		selector: "#uae-whats-new",
		uniqueKey: "uael-whats-new",
		triggerButton: {
			beforeBtn:
				'<div class="w-4 sm:w-8 h-8 sm:h-10 flex items-center whitespace-nowrap justify-center cursor-pointer rounded-full border border-slate-200">',
			icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#434141" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-megaphone"><path d="m3 11 18-5v12L3 14v-3z"></path><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"></path></svg>',
			afterBtn: "</div>",
		},
		flyout: {
			title: __("What's New?", "uael"),
			formatDate: (date) => {
				const dayOfWeek = date.toLocaleDateString("en-US", {
					weekday: "long",
				});
				const month = date.toLocaleDateString("en-US", {
					month: "long",
				});
				const day = date.getDate();
				const year = date.getFullYear();
				return `${dayOfWeek} ${month} ${day}, ${year}`;
			},
		},
	});
	return (
		<Topbar
			className="uae-nav-menu relative"
			style={{ width: "unset", padding: "0.5rem", zIndex: "9" }}
		>
			<div className="flex flex-col lg:flex-row items-start md:items-center w-full">
				<div className="flex flex-row md:items-center md:gap-8 w-full">
					<Topbar.Left>
						<Topbar.Item>
							{uaelSettingsData.icon_url && (
								<Link to={routes.dashboard.path}>
									<img
										src={`${uaelSettingsData.icon_url}`}
										alt="Logo"
										className="ml-4 cursor-pointer"
										style={{
											height: "35px",
											width: "35px",
										}}
									/>
								</Link>
							)}
						</Topbar.Item>
					</Topbar.Left>
					<Topbar.Middle className="flex-grow" align="left">
						<nav className="flex flex-wrap gap-6 mt-2 md:mt-0 cursor-pointer">
							<Link
								to={routes.dashboard.path}
								className={`${
									isActive("dashboard") ? "active-link" : ""
								}`}
								style={linkStyle("dashboard")}
							>
								{__("Dashboard", "uael")}
							</Link>
							{uaelSettingsData.is_lite_active && (
								<Link
									to={routes.headerFooterBuilder.path} // Add this Link component.
									className={`${
										isActive(
											"edit.php?post_type=elementor-hf"
										)
											? "active-link"
											: ""
									}`}
									style={linkStyle(
										"edit.php?post_type=elementor-hf"
									)}
								>
									{__("Header & Footer", "uael")}
								</Link>
							)}
							<Link
								to={routes.widgets.path}
								className={`${
									isActive("widgets") ? "active-link" : ""
								}`}
								style={linkStyle("widgets")}
							>
								{__("Widgets", "uael")}
							</Link>
							<Link
								to={routes.settings.path}
								className={`${
									isActive("settings") ? "active-link" : ""
								}`}
								style={linkStyle("settings")}
							>
								{__("Settings", "uael")}
							</Link>
						</nav>
					</Topbar.Middle>
					<Topbar.Right className="flex flex-col lg:flex-row gap-4 mt-4 md:mt-0">
						<Topbar.Item>
							<DropdownMenu
								placement="bottom-start"
								isOpen={isDropdownOpen}
								onOpenChange={setIsDropdownOpen}
							>
								<DropdownMenu.Trigger>
									<Badge
										label={__("PRO", "uael")}
										size="md"
										variant="neutral"
										className="bg-gradient-to-r from-[#A78BFA] to-[#6005FF] text-white hidden md:flex"
									/>
								</DropdownMenu.Trigger>
								<DropdownMenu.ContentWrapper>
									<DropdownMenu.Content className="w-52">
										<DropdownMenu.List>
											<DropdownMenu.Item>
												{__("Version", "uael")}
											</DropdownMenu.Item>
											{uaelSettingsData.is_lite_active && (
												<DropdownMenu.Item>
													<div className="flex justify-between w-full">
														{`${uaelSettingsData.uaelite_current_version}`}
														<img
															src={`${uaelSettingsData.core_badge}`}
															alt="CORE"
															style={{
																height: "30px",
																width: "38px",
															}}
														/>
													</div>
												</DropdownMenu.Item>
											)}
											<DropdownMenu.Item>
												<div className="flex justify-between w-full">
													{`${uaelSettingsData.uael_current_version}`}
													<img
														src={`${uaelSettingsData.pro_badge}`}
														alt="PRO"
														style={{
															height: "30px",
															width: "38px",
														}}
													/>
												</div>
											</DropdownMenu.Item>
										</DropdownMenu.List>
									</DropdownMenu.Content>
								</DropdownMenu.ContentWrapper>
							</DropdownMenu>
						</Topbar.Item>
						{!uaelSettingsData.hide_settings && (
							<Topbar.Item className="gap-4 flex items-center uae-help-dropdown relative">
								<div
									onClick={toggleHelpDropdown}
									className="cursor-pointer"
									title="Help"
								>
									<CircleHelp />
								</div>
								{isHelpOpen && (
									<div className="absolute right-0 top-8 w-64 bg-white border border-gray-200 rounded shadow-md z-50">
										<ul className="text-sm text-gray-700">
											<li className="px-4 py-2 border-b font-semibold">
												{__("Useful Resources", "uael")}
											</li>
											<li
												className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex gap-2 items-center"
												onClick={() =>
													handleRedirect(
														"https://ultimateelementor.com/docs/getting-started-uael/"
													)
												}
											>
												<FileText />
												{__("Getting Started", "uael")}
											</li>
											<li
												className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex gap-2 items-center"
												onClick={() =>
													handleRedirect(
														"https://ultimateelementor.com/docs-category/widgets/"
													)
												}
											>
												<FileText />
												{__(
													"How to use widgets",
													"uael"
												)}
											</li>
											<li
												className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex gap-2 items-center"
												onClick={() =>
													handleRedirect(
														"https://ultimateelementor.com/docs-category/features/"
													)
												}
											>
												<FileText />
												{__(
													"How to use features",
													"uael"
												)}
											</li>
											<li
												className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex gap-2 items-center"
												onClick={() =>
													handleRedirect(
														"https://ultimateelementor.com/docs-category/templates/"
													)
												}
											>
												<FileText />
												{__(
													"How to use templates",
													"uael"
												)}
											</li>
											<li
												className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex gap-2 items-center"
												onClick={() =>
													handleRedirect(
														"https://ultimateelementor.com/contact/"
													)
												}
											>
												<Headset />
												{__("Contact us", "uael")}
											</li>
										</ul>
									</div>
								)}
								<div className="pb-1" id="uae-whats-new"></div>
								<Link to={routes.settings.path}>
									<User
										className="cursor-pointer uae-user-icon"
										style={{ color: "black" }}
									/>
								</Link>
							</Topbar.Item>
						)}
					</Topbar.Right>
				</div>
			</div>
		</Topbar>
	);
}
